/**
 * Country Status Gate
 *
 * Renders a full-screen maintenance / unavailable screen when a country's
 * lifecycle status is not `active`. Wraps regional landing pages.
 *
 * - active       → renders children unchanged
 * - maintenance  → blocks public browsing, shows maintenance card + Sign-In CTA
 *                  (existing users may still log in)
 * - disabled     → blocks everything for public, shows unavailable card
 *                  (also injects <meta name="robots" content="noindex"/>)
 *
 * Admin/super-admin sessions bypass the gate so they can keep working.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldAlert, ShieldX, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_ROLES = new Set(['admin', 'super_admin']);

const setRobotsNoIndex = (noIndex) => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector('meta[name="robots"]');
  if (noIndex) {
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'robots');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', 'noindex, nofollow');
  } else if (tag && tag.getAttribute('content') === 'noindex, nofollow') {
    tag.remove();
  }
};

const StatusScreen = ({ variant, country, message }) => {
  const isDisabled = variant === 'disabled';
  const Icon = isDisabled ? ShieldX : ShieldAlert;
  const accent = isDisabled ? 'text-red-300' : 'text-amber-300';
  const ring = isDisabled ? 'ring-red-500/30 bg-red-500/5' : 'ring-amber-500/30 bg-amber-500/5';

  return (
    <div className="min-h-screen bg-[#060606] text-white flex items-center justify-center px-6" data-testid={`country-${variant}-screen`}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-lg w-full text-center"
      >
        <div className="flex justify-center mb-8">
          <Logo to={null} size="lg" wordmarkClassName="text-gold" />
        </div>

        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ring-2 ${ring} mb-6`}>
          <Icon className={`w-7 h-7 ${accent}`} />
        </div>

        <h1 className="font-playfair text-3xl md:text-4xl font-light mb-3" data-testid="country-status-heading">
          {isDisabled
            ? `${country} is temporarily unavailable`
            : `${country} is undergoing maintenance`}
        </h1>

        <p className="text-white/60 text-base leading-relaxed mb-8" data-testid="country-status-message">
          {message
            || (isDisabled
              ? 'The platform is currently offline for this region. All member data is preserved and the experience will return shortly.'
              : 'We are upgrading the experience for this region. Existing members can still sign in. New registrations and public browsing are paused until we are back.')}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {!isDisabled && (
            <Link
              to="/login"
              className="bg-[#C6A15B] text-black px-8 py-3 uppercase tracking-[0.2em] text-[12px] font-semibold hover:bg-[#DCC286] transition-colors"
              data-testid="country-status-signin-link"
            >
              Member Sign In
            </Link>
          )}
          <Link
            to="/"
            className="border border-white/15 text-white px-8 py-3 uppercase tracking-[0.2em] text-[12px] hover:bg-white/5 transition-colors"
            data-testid="country-status-home-link"
          >
            Switch Region
          </Link>
        </div>

        <p className="text-white/25 text-[11px] tracking-[0.25em] uppercase mt-12">
          © 2026 ASTIIV BASGAT · {country}
        </p>
      </motion.div>
    </div>
  );
};

const CountryStatusGate = ({ region, countryLabel, children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/countries/${region}/status`);
        if (cancelled) return;
        setStatus(data?.status || 'active');
        setMessage(data?.status_message || '');
      } catch {
        if (!cancelled) setStatus('active'); // fail-open to avoid locking out users on transient errors
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [region]);

  // Inject noindex meta when disabled
  useEffect(() => {
    setRobotsNoIndex(status === 'disabled');
    return () => setRobotsNoIndex(false);
  }, [status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060606] flex items-center justify-center text-gold">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  // Admins/super_admins always see the real page (so they can fix what's broken)
  const bypass = user && ADMIN_ROLES.has(user.role);
  if (bypass || status === 'active') {
    return children;
  }

  return <StatusScreen variant={status} country={countryLabel} message={message} />;
};

export default CountryStatusGate;
